import { Component } from '@theme/component';
import { updateAllHeaderCustomProperties } from '@theme/utilities';

/**
 * Announcement bar: continuous marquee display + optional dismiss.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [dismissButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#restoreDismissedState();

    if (this.hasAttribute('dismissible') && this.refs.dismissButton) {
      this.refs.dismissButton.addEventListener('click', this.#handleDismiss);
    }

    this.#setupMarquee();
    this.#resizeObserver = new ResizeObserver(() => this.#setupMarquee());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.refs.dismissButton?.removeEventListener('click', this.#handleDismiss);
    this.#resizeObserver?.disconnect();
  }

  #setupMarquee() {
    const track = this.querySelector('[data-announcement-track]');
    const items = this.querySelector('[data-announcement-items]');
    if (!(track instanceof HTMLElement) || !(items instanceof HTMLElement)) return;

    // Reset clones so measurements stay accurate across resizes / editor updates.
    track.querySelectorAll('[data-announcement-clone]').forEach((node) => node.remove());
    items.querySelectorAll('[data-announcement-fill]').forEach((node) => node.remove());
    track.removeAttribute('data-marquee-ready');

    const sourceItems = [...items.querySelectorAll('[data-announcement-item]')];
    if (sourceItems.length === 0) return;

    const viewportWidth = this.querySelector('[data-announcement-marquee]')?.clientWidth || this.clientWidth;
    // Duplicate source messages until the set is at least as wide as the viewport.
    while (items.scrollWidth < viewportWidth && items.children.length < 40) {
      for (const item of sourceItems) {
        const fill = /** @type {HTMLElement} */ (item.cloneNode(true));
        fill.removeAttribute('data-shopify-editor-block');
        fill.setAttribute('data-announcement-fill', '');
        fill.setAttribute('aria-hidden', 'true');
        items.appendChild(fill);
        if (items.scrollWidth >= viewportWidth) break;
      }
    }

    // Seamless loop: second identical set so -50% translate loops cleanly.
    const clone = /** @type {HTMLElement} */ (items.cloneNode(true));
    clone.setAttribute('data-announcement-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
      el.removeAttribute('data-shopify-editor-block');
    });
    track.appendChild(clone);

    const speedSetting = Number(this.dataset.speed || 5);
    // Higher editor "speed" value = slower scroll. Target ~35–90 px/s.
    const pixelsPerSecond = Math.max(28, 110 / Math.max(speedSetting, 1));
    const duration = Math.max(12, items.scrollWidth / pixelsPerSecond);

    this.style.setProperty('--announcement-marquee-duration', `${duration.toFixed(2)}s`);
    track.setAttribute('data-marquee-ready', 'true');
  }

  #dismissStorageKey() {
    const sectionId = this.dataset.sectionId;
    return sectionId ? `announcement-bar-dismissed-${sectionId}` : null;
  }

  #restoreDismissedState() {
    const storageKey = this.#dismissStorageKey();
    if (!storageKey || localStorage.getItem(storageKey) !== '1') return;

    this.#hideAnnouncementSection();
  }

  #handleDismiss = () => {
    const storageKey = this.#dismissStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, '1');
    }

    this.#hideAnnouncementSection();
    updateAllHeaderCustomProperties();
  };

  #hideAnnouncementSection() {
    this.closest('.shopify-section')?.classList.add('announcement-bar--dismissed');
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
